import { WidePicture } from "assets/appConfig";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import { ImageSelectionDialog } from "components/ImageHandler";
import { ImageSelectionResult } from "components/ImageHandler/ImageSelectionDialog";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiInfo from "components/MuiExtras/Typography/MuiInfo";
import { SyncIndicator } from "components/SyncIndicator";
import { AsyncUploader } from "lib/AsyncUploader";
import { AspectRatio } from "models/ApiEnums";
import { AsyncUploadModel } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IUploadProps {
    acceptedFiles?: string[];
    allowEditSelectedImage?: boolean;
    boxHeight?: number;
    createThumbWithAspectRatio?: AspectRatio;
    dimensions?: ImageDimensions;
    enableClear?: boolean;
    selectButtomText?: string;
    variant?: "normal" | "boxed";
    initialSource?: "library" | "local";

    currentImageUrl?: string;
    emptyImageUrl?: string;

    onClear?: () => void;
    onFileUploadStarted?: () => void;
    onFileUploadFinished?: () => void;
    onFileUploadProgress?: (progress: number) => void;
    onFileUploadSuccess?: (data: AsyncUploadModel) => void;
    onFileUploadError?: (data: any) => void;
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const RenderBox: React.FC<{ boxHeight?: number; }> = (props) => {
    return (
        <div
            style={
                {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    backgroundColor: "#e5e5e5",
                    border: "solid 1px #808080",
                    padding: "8px",
                    height: props.boxHeight || "auto",
                }
            }
        >
            {props.children}
        </div>
    );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

const RenderImage: React.FC<{ url: string, width: number, height: number; }> = (props) => {
    return (
        <img
            style={
                {
                    height: props.height || "inherit",
                    width: props.width || "inherit"
                }
            }
            alt=""
            src={props.url}
        />
    );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IDisplayImageProps {
    url: string;
    width?: number;
    height?: number;
    isEmpty: boolean;
    uploading: boolean;

    enableClear?: boolean;
    boxHeight?: number;
    variant?: "normal" | "boxed";
    onClear?: () => void;
}


interface IDisplayImageState {
    mouseOver: boolean;
}


class DisplayImage extends React.PureComponent<IDisplayImageProps, IDisplayImageState>{

    constructor(props: IDisplayImageProps) {
        super(props);
        this.state = {
            mouseOver: false,
        };
    }

    private renderClear() {
        if (!this.props.enableClear || this.props.isEmpty || this.state.mouseOver == false)
            return null;

        return (
            <div style={{ position: "absolute", right: 0 }}>
                <IconButtonDelete color="error" size="small" onClick={this.props.onClear} />
            </div>
        );
    }

    private renderUploading() {
        if (!this.props.uploading)
            return null;

        return (
            <div style={{ position: "absolute", right: 0 }}>
                <MuiInfo><SyncIndicator /></MuiInfo>
            </div>
        );
    }
    public render() {
        return (
            <div style={{ position: "relative" }}
                onMouseEnter={() => { this.setState({ mouseOver: true }); }}
                onMouseLeave={() => { this.setState({ mouseOver: false }); }}
            >
                {this.renderClear()}

                {this.renderUploading()}

                {
                    this.props.variant == "boxed"
                        ? <RenderBox boxHeight={this.props.boxHeight}><RenderImage url={this.props.url} width={this.props.width} height={this.props.height} /></RenderBox>
                        : <RenderImage url={this.props.url} width={this.props.width} height={this.props.height} />
                }
            </div>
        );
    }

}

/////////////////////////////////////////////////////////////////////////////////////////////////

type ImageSelectionProps = IUploadProps & WithTranslation;

interface IUploadState {
    dialog: any;
    currentPreviewUrl: any;
    lastUpload: AsyncUploadModel;
    uploadId: string;
    uploading: boolean;
}

class ImageSelectionComponent extends React.PureComponent<ImageSelectionProps, IUploadState> {

    private asyncUploader: AsyncUploader;

    constructor(props: ImageSelectionProps) {
        super(props);
        this.state = {
            uploadId: new Date().getTime().toString(),
            currentPreviewUrl: null,
            lastUpload: null,
            dialog: null,
            uploading: false
        };

        this.asyncUploader = new
            AsyncUploader({
                onFileUploadSuccess: this.onFileUploadSuccess,
                onFileStarted: () => this.setState({ uploading: true }, () => this.props.onFileUploadStarted && this.props.onFileUploadStarted()),
                onFileFinished: () => this.setState({ uploading: false }, () => this.props.onFileUploadFinished && this.props.onFileUploadFinished()),
                onFileUploadProgress: this.props.onFileUploadProgress,
                onFileUploadError: this.props.onFileUploadError
            });
    }

    static defaultProps: Partial<ImageSelectionProps> = {
        acceptedFiles: ["*"],
        allowEditSelectedImage: false,
        initialSource: "library",
        variant: "normal",
    };

    //
    private handleClear = () => {
        this.setState({
            currentPreviewUrl: null,
            lastUpload: null
        }, () => this.props.onClear && this.props.onClear());
    };

    //
    private getImageUrl(state: IUploadState, props: IUploadProps): string {
        return state.currentPreviewUrl
            ? state.currentPreviewUrl
            : props.currentImageUrl
                ? props.currentImageUrl
                : props.emptyImageUrl;
    }

    private onFileUploadSuccess = (data: AsyncUploadModel) => {
        const oldUploadCompleted = this.state.lastUpload;

        this.setState({
            lastUpload: data,
            currentPreviewUrl: data.thumbnail
                ? data.thumbnail.tempUrl
                : data.mainFile
                    ? data.mainFile.tempUrl
                    : this.props.emptyImageUrl
        });

        if (this.props.onFileUploadSuccess)
            this.props.onFileUploadSuccess(data);

        if (oldUploadCompleted) {
            var files = [oldUploadCompleted.mainFile.fileId];
            if (oldUploadCompleted.thumbnail != null) files.push(oldUploadCompleted.thumbnail.fileId);
            this.asyncUploader.delete(files);
        }

    };

    //
    private handleChooseImage = (image: ImageSelectionResult) => {

        if (image == null) {
            this.setState({ dialog: null });
        }
        else {

            var finalImage = (this.props.allowEditSelectedImage) ? image.editedImage : image.source;

            this.setState({
                dialog: null,
                currentPreviewUrl: finalImage ? finalImage.sourceDataOrUrl : null
            }, () => {

                this.asyncUploader.uploadImage(finalImage, (!this.props.allowEditSelectedImage && this.props.createThumbWithAspectRatio));

            });
        }
    };

    ////////////////////////////////////////////////////////////

    render() {
        const url = this.getImageUrl(this.state, this.props);
        const dimensions = this.props.dimensions || WidePicture;

        return (
            <>
                {this.state.dialog}

                <div style={{ textAlign: "center" }}>

                    <DisplayImage
                        url={url}
                        isEmpty={url == null || url == this.props.emptyImageUrl}
                        uploading={this.state.uploading}
                        width={dimensions.width}
                        height={dimensions.height}
                        boxHeight={this.props.boxHeight}
                        enableClear={this.props.enableClear}
                        onClear={this.handleClear}
                        variant={this.props.variant}
                    />

                    <br />
                    <MuiRegularButton color="info"
                        onClick={
                            () => {
                                this.setState({
                                    dialog: (
                                        <ImageSelectionDialog
                                            acceptedFiles={this.props.acceptedFiles}
                                            initialSource={this.props.initialSource}
                                            allowEdit={this.props.allowEditSelectedImage}
                                            dimensions={dimensions}
                                            onClose={this.handleChooseImage}
                                        />
                                    )
                                });
                            }
                        }
                    >
                        {this.props.selectButtomText || this.props.t("Common.Buttons.SelectText")}
                    </MuiRegularButton>
                </div>
            </>
        );
    }
}

export const ImageSelection = withTranslation()(ImageSelectionComponent);